import { useState } from "react";
import { Link } from "react-router";
import { TabBar } from "../../components/TabBar";
import { Button } from "../../components/Button";
import { Sheet } from "../../components/Sheet";
import { Field, inputShell } from "../../components/Field";

interface ProfileScreenProps {
  firstName: string | null;
  email: string;
  phone: string;
  exam: string;
  examYear: number;
  hasAllAccess: boolean;
  validUntil?: string | undefined;
  subjects: string[];
  /** What the offline downloads take, already formatted: "4.2 MB". */
  downloadsSize: string;
  onSaveName?: ((firstName: string) => Promise<void>) | undefined;
  onLogout?: (() => void) | undefined;
  onChangePassword?: ((current: string, newPassword: string) => Promise<void>) | undefined;
  onDeleteAccount?: (() => Promise<void>) | undefined;
  onDownloads?: (() => void) | undefined;
  onChangeSubjects?: (() => void) | undefined;
  /** The exams this student sits, primary first. */
  exams?: { examType: "JAMB" | "WAEC"; examYear: number | null }[] | undefined;
  /** Start the other exam: the year here, its subjects on the next screen. */
  onAddExam?: ((exam: "JAMB" | "WAEC", examYear: number) => Promise<void>) | undefined;
}

const MIN_PASSWORD = 6;

/**
 * Screen 18 — identity, entitlement, subjects, settings, danger zone.
 *
 * "Delete my account" is separated and red, not hidden. NDPA gives the student
 * that right; burying it would be a dark pattern, and a parent asking us to
 * delete a child's data should not need support to find it.
 */
export function ProfileScreen({
  firstName,
  email,
  exam,
  examYear,
  hasAllAccess,
  validUntil,
  subjects,
  downloadsSize,
  onSaveName,
  onLogout,
  onChangePassword,
  onDeleteAccount,
  onDownloads,
  onChangeSubjects,
  exams = [],
  onAddExam,
}: ProfileScreenProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(firstName ?? "");
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState(firstName);

  // Change password sheet
  const [pwSheet, setPwSheet] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  // Delete account sheet
  const [deleteSheet, setDeleteSheet] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Add the other exam
  const thisYear = new Date().getFullYear();
  const other = exams.length === 1 ? (exams[0]!.examType === "JAMB" ? "WAEC" : "JAMB") : null;
  const [addSheet, setAddSheet] = useState(false);
  const [addYear, setAddYear] = useState(thisYear + 1);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const addExam = async () => {
    if (!other || adding) return;
    setAdding(true);
    setAddError(null);
    try {
      await onAddExam?.(other, addYear);
      setAddSheet(false);
    } catch (error) {
      setAddError(error instanceof Error ? error.message : "Couldn't add it. Try again.");
    } finally {
      setAdding(false);
    }
  };

  const saveName = async () => {
    if (!draft.trim() || saving) return;
    setSaving(true);
    try {
      await onSaveName?.(draft.trim());
      setDisplayName(draft.trim());
      setEditing(false);
    } catch {
      // Silent — the student can try again.
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    setPwError(null);
    if (newPw.length < MIN_PASSWORD) {
      setPwError(`New password needs at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (newPw !== confirmPw) {
      setPwError("Passwords don't match.");
      return;
    }
    setPwSaving(true);
    try {
      await onChangePassword?.(currentPw, newPw);
      setPwSuccess(true);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (error) {
      setPwError(error instanceof Error ? error.message : "Couldn't change password. Try again.");
    } finally {
      setPwSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await onDeleteAccount?.();
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div className="app-column">
      <div className="flex-1 px-5 pb-6 pt-6">
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {editing ? (
              <div className="flex flex-col gap-2">
                <label htmlFor="profile-name" className="sr-only">
                  First name
                </label>
                <input
                  id="profile-name"
                  type="text"
                  autoFocus
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="Your Nickname"
                  maxLength={100}
                  className="w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-[16px] font-bold text-text outline-none placeholder:text-text-muted focus:border-accent"
                />
                <div className="flex gap-2">
                  <Button
                    className="!w-auto !min-h-9 px-4 text-[13px]"
                    disabled={!draft.trim() || saving}
                    onClick={() => void saveName()}
                  >
                    {saving ? "Saving…" : "Save"}
                  </Button>
                  <Button
                    variant="ghost"
                    className="!w-auto !min-h-9 px-4 text-[13px]"
                    onClick={() => {
                      setDraft(displayName ?? "");
                      setEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className="m-0 font-[family-name:var(--font-display)] text-[22px] font-bold">
                  {displayName || "Add your nickname"}
                </p>
                <p className="tabular m-0 pt-0.5 text-[12.5px] text-text-muted">
                  {email} · {exam} {examYear}
                </p>
              </>
            )}
          </div>
          {!editing ? (
            <button
              type="button"
              onClick={() => {
                setDraft(displayName ?? "");
                setEditing(true);
              }}
              className="shrink-0 text-[13px] font-semibold text-accent"
            >
              Edit
            </button>
          ) : null}
        </header>

        {!displayName && !editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="mt-3 w-full rounded-[var(--radius-card)] border border-accent bg-accent-tint p-3 text-left text-[13px] text-accent"
          >
            Add your nickname so we know what to call you →
          </button>
        ) : null}

        {hasAllAccess ? (
          <div className="mt-5 flex items-center justify-between rounded-[var(--radius-card)] border border-positive bg-[var(--positive-tint)] p-4">
            <span className="text-[14px] font-semibold">All-access</span>
            {validUntil ? (
              <span className="tabular text-[12.5px] text-text-muted">Valid till {validUntil}</span>
            ) : null}
          </div>
        ) : (
          <Link
            to="/upgrade"
            className="mt-5 flex items-center justify-between rounded-[var(--radius-card)] border border-accent bg-accent-tint p-4"
          >
            <span className="flex flex-col gap-0.5">
              <span className="text-[14px] font-semibold">Unlock everything</span>
              <span className="text-[12px] text-text-muted">
                One payment, no renewals — till your exam
              </span>
            </span>
            <span aria-hidden="true" className="text-accent">
              →
            </span>
          </Link>
        )}

        {exams.length > 0 ? (
          <section className="pt-6">
            <h2 className="m-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
              Your exams
            </h2>
            <ul className="m-0 flex list-none flex-wrap gap-2 p-0 pt-3">
              {exams.map((entry) => (
                <li
                  key={entry.examType}
                  className={`tabular rounded-[var(--radius-chip)] border px-3 py-2 text-[12.5px] ${
                    entry.examType === exam
                      ? "border-accent bg-accent-tint font-semibold"
                      : "border-border bg-surface"
                  }`}
                >
                  {entry.examType}
                  {entry.examYear ? ` ${entry.examYear}` : ""}
                </li>
              ))}
            </ul>
            {other && onAddExam ? (
              <button
                type="button"
                onClick={() => {
                  setAddError(null);
                  setAddSheet(true);
                }}
                className="mt-3 w-full rounded-[var(--radius-card)] border border-dashed border-border-strong p-3 text-left text-[13px] text-accent"
              >
                Also sitting {other}? Add it — same payment covers both →
              </button>
            ) : null}
          </section>
        ) : null}

        <section className="pt-6">
          <div className="flex items-center justify-between">
            <h2 className="m-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
              {exams.length > 1 ? `My ${exam} subjects` : "My subjects"}
            </h2>
            <button
              type="button"
              onClick={onChangeSubjects}
              className="text-[12.5px] font-semibold text-accent"
            >
              Change
            </button>
          </div>
          <ul className="m-0 flex list-none flex-wrap gap-2 p-0 pt-3">
            {subjects.map((subject) => (
              <li
                key={subject}
                className="rounded-[var(--radius-chip)] border border-border bg-surface px-3 py-2 text-[12.5px]"
              >
                {subject}
              </li>
            ))}
          </ul>
        </section>

        <ul className="m-0 mt-6 flex list-none flex-col gap-px overflow-hidden rounded-[var(--radius-card)] border border-border p-0 [&>li]:bg-surface">
          <li>
            <button
              type="button"
              onClick={onDownloads}
              className="flex w-full items-center justify-between px-4 py-3.5 text-left text-[14px]"
            >
              Offline downloads
              <span className="tabular text-[12.5px] text-text-muted">{downloadsSize}</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => {
                setPwSheet(true);
                setPwError(null);
                setPwSuccess(false);
                setCurrentPw("");
                setNewPw("");
                setConfirmPw("");
              }}
              className="w-full px-4 py-3.5 text-left text-[14px]"
            >
              Change password
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={onLogout}
              className="w-full px-4 py-3.5 text-left text-[14px]"
            >
              Log out
            </button>
          </li>
        </ul>

        <p className="m-0 pt-5 text-center text-[11.5px] text-text-muted">
          <Link to="/privacy" className="text-text-muted underline">
            Privacy
          </Link>
          {" · "}
          <Link to="/terms" className="text-text-muted underline">
            Terms
          </Link>
        </p>

        <div className="pt-6">
          <button
            type="button"
            onClick={() => setDeleteSheet(true)}
            className="min-h-11 w-full rounded-[var(--radius-control)] border border-negative px-4 text-[13px] font-semibold text-negative"
          >
            Delete my account
          </button>
        </div>
      </div>

      <TabBar />

      {/* ---- Add the other exam ---- */}
      {addSheet && other ? (
        <Sheet
          title={`Add ${other}`}
          onDismiss={() => setAddSheet(false)}
          actions={
            <>
              <Button disabled={adding} onClick={() => void addExam()}>
                {adding ? "Adding…" : "Next: pick subjects"}
              </Button>
              <Button variant="ghost" onClick={() => setAddSheet(false)}>
                Cancel
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <p className="m-0 text-[13px] leading-[1.5] text-text-muted">
              {other} gets its own subjects, readiness and practice. Switch between your exams at
              the top of any tab.
            </p>
            {addError ? (
              <p role="alert" className="m-0 text-[12.5px] text-negative">
                {addError}
              </p>
            ) : null}
            <p className="m-0 text-[12.5px] font-semibold">When are you sitting it?</p>
            <div role="group" aria-label="Exam year" className="flex gap-2">
              {[thisYear, thisYear + 1, thisYear + 2].map((year) => (
                <button
                  key={year}
                  type="button"
                  aria-pressed={addYear === year}
                  onClick={() => setAddYear(year)}
                  className={`tabular min-h-11 flex-1 rounded-[var(--radius-chip)] border text-[13.5px] ${
                    addYear === year
                      ? "border-accent bg-accent-tint font-semibold"
                      : "border-border bg-surface text-text-muted"
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </Sheet>
      ) : null}

      {/* ---- Change password sheet ---- */}
      {pwSheet ? (
        <Sheet
          title="Change password"
          onDismiss={() => setPwSheet(false)}
          actions={
            pwSuccess ? (
              <Button onClick={() => setPwSheet(false)}>Done</Button>
            ) : (
              <>
                <Button
                  disabled={!currentPw || !newPw || !confirmPw || pwSaving}
                  onClick={() => void savePassword()}
                >
                  {pwSaving ? "Saving…" : "Change password"}
                </Button>
                <Button variant="ghost" onClick={() => setPwSheet(false)}>
                  Cancel
                </Button>
              </>
            )
          }
        >
          {pwSuccess ? (
            <p className="m-0 text-[14px] text-positive">Password changed successfully.</p>
          ) : (
            <div className="flex flex-col gap-4">
              {pwError ? (
                <p role="alert" className="m-0 text-[12.5px] text-negative">
                  {pwError}
                </p>
              ) : null}
              <Field label="Current password" htmlFor="pw-current">
                <div className={inputShell}>
                  <input
                    id="pw-current"
                    type="password"
                    autoComplete="current-password"
                    value={currentPw}
                    onChange={(event) => setCurrentPw(event.target.value)}
                    className="w-full bg-transparent text-[15px] text-text outline-none"
                  />
                </div>
              </Field>
              <Field
                label="New password"
                htmlFor="pw-new"
                hint={`At least ${MIN_PASSWORD} characters`}
              >
                <div className={inputShell}>
                  <input
                    id="pw-new"
                    type="password"
                    autoComplete="new-password"
                    value={newPw}
                    onChange={(event) => setNewPw(event.target.value)}
                    className="w-full bg-transparent text-[15px] text-text outline-none"
                  />
                </div>
              </Field>
              <Field label="Confirm new password" htmlFor="pw-confirm">
                <div className={inputShell}>
                  <input
                    id="pw-confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPw}
                    onChange={(event) => setConfirmPw(event.target.value)}
                    className="w-full bg-transparent text-[15px] text-text outline-none"
                  />
                </div>
              </Field>
              <Link to="/reset" className="text-[12.5px] font-semibold text-accent">
                Forgot your current password?
              </Link>
            </div>
          )}
        </Sheet>
      ) : null}

      {/* ---- Delete account sheet ---- */}
      {deleteSheet ? (
        <Sheet
          title="Delete your account?"
          onDismiss={() => setDeleteSheet(false)}
          actions={
            <>
              <button
                type="button"
                disabled={deleting}
                onClick={() => void confirmDelete()}
                className="min-h-12 w-full rounded-[var(--radius-control)] border border-negative px-4 text-[15px] font-semibold text-negative disabled:opacity-40"
              >
                {deleting ? "Deleting…" : "Delete my account"}
              </button>
              <Button variant="ghost" onClick={() => setDeleteSheet(false)}>
                Keep my account
              </Button>
            </>
          }
        >
          <p className="m-0 text-[13.5px] leading-[1.55] text-text-muted">
            This will permanently delete your account and all your practice data. Your readiness
            scores, streak, and everything else will be gone. This cannot be undone.
          </p>
        </Sheet>
      ) : null}
    </div>
  );
}
